import React from 'react';
import { ArrowLeft, Award, GraduationCap, Briefcase, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/pageTranslations';

const Curriculum = () => {
  const navigate = useNavigate();
  const lang = useLanguage();

  const texts = {
    es: {
      backTo: 'Volver a Dr. Norro',
      title: 'Currículum',
      subtitle: 'Dr. Ángel Ruiz Norro',
      academicTitle: 'Formación Académica',
      academic: [
        { title: 'Licenciado en Medicina y Cirugía', desc: 'Universidad de Granada' },
        { title: 'Especialización en Cirugía Plástica', desc: 'Formación en Brasil con los mejores especialistas' },
        { title: 'Máster en Medicina Estética', desc: 'Formación continua y actualización permanente' },
      ],
      experienceTitle: 'Experiencia Profesional',
      experience: [
        { title: '+30 años de experiencia', desc: 'En cirugía estética y medicina estética' },
        { title: 'Pionero en Rinomodelación en España', desc: 'Realizando esta técnica desde 2007' },
        { title: 'Práctica privada', desc: 'En Marbella, Madrid y Barcelona' },
      ],
      recognitionsTitle: 'Reconocimientos y Afiliaciones',
      recognitions: [
        { title: 'Sociedad Española de Medicina Estética (SEME)', desc: 'Miembro activo y ponente en congresos' },
        { title: 'Colegio de Médicos de Málaga', desc: 'Colegiado nº XXXX' },
      ],
      internationalTitle: 'Proyección Internacional',
      internationalText: 'El Dr. Norro ha desarrollado técnicas propias de rinomodelación que han sido reconocidas internacionalmente. Ha impartido formación a otros profesionales y participa regularmente en congresos nacionales e internacionales compartiendo su experiencia.',
      cta: 'CONSULTAR CON EL DR. NORRO',
    },
    en: {
      backTo: 'Back to Dr. Norro',
      title: 'Curriculum Vitae',
      subtitle: 'Dr. Ángel Ruiz Norro',
      academicTitle: 'Academic Background',
      academic: [
        { title: 'Degree in Medicine and Surgery', desc: 'University of Granada' },
        { title: 'Specialization in Plastic Surgery', desc: 'Training in Brazil with top specialists' },
        { title: 'Master in Aesthetic Medicine', desc: 'Continuous training and permanent updates' },
      ],
      experienceTitle: 'Professional Experience',
      experience: [
        { title: '+30 years of experience', desc: 'In aesthetic surgery and aesthetic medicine' },
        { title: 'Pioneer in Rhinomodeling in Spain', desc: 'Performing this technique since 2007' },
        { title: 'Private practice', desc: 'In Marbella, Madrid and Barcelona' },
      ],
      recognitionsTitle: 'Awards and Affiliations',
      recognitions: [
        { title: 'Spanish Society of Aesthetic Medicine (SEME)', desc: 'Active member and congress speaker' },
        { title: 'Medical Association of Málaga', desc: 'Member nº XXXX' },
      ],
      internationalTitle: 'International Reach',
      internationalText: 'Dr. Norro has developed proprietary rhinomodeling techniques that have been internationally recognized. He has trained other professionals and regularly participates in national and international conferences sharing his experience.',
      cta: 'CONSULT WITH DR. NORRO',
    },
    fr: {
      backTo: 'Retour à Dr. Norro',
      title: 'Curriculum Vitae',
      subtitle: 'Dr. Ángel Ruiz Norro',
      academicTitle: 'Formation Académique',
      academic: [
        { title: 'Diplômé en Médecine et Chirurgie', desc: 'Université de Grenade' },
        { title: 'Spécialisation en Chirurgie Plastique', desc: 'Formation au Brésil avec les meilleurs spécialistes' },
        { title: 'Master en Médecine Esthétique', desc: 'Formation continue et mise à jour permanente' },
      ],
      experienceTitle: 'Expérience Professionnelle',
      experience: [
        { title: '+30 ans d\'expérience', desc: 'En chirurgie esthétique et médecine esthétique' },
        { title: 'Pionnier de la Rhinomodelage en Espagne', desc: 'Pratiquant cette technique depuis 2007' },
        { title: 'Pratique privée', desc: 'À Marbella, Madrid et Barcelone' },
      ],
      recognitionsTitle: 'Reconnaissances et Affiliations',
      recognitions: [
        { title: 'Société Espagnole de Médecine Esthétique (SEME)', desc: 'Membre actif et conférencier' },
        { title: 'Ordre des Médecins de Málaga', desc: 'Membre nº XXXX' },
      ],
      internationalTitle: 'Projection Internationale',
      internationalText: 'Le Dr. Norro a développé des techniques de rhinomodelage propres qui ont été reconnues internationalement. Il a formé d\'autres professionnels et participe régulièrement à des congrès nationaux et internationaux pour partager son expérience.',
      cta: 'CONSULTER LE DR. NORRO',
    },
    pt: {
      backTo: 'Voltar para Dr. Norro',
      title: 'Currículo',
      subtitle: 'Dr. Ángel Ruiz Norro',
      academicTitle: 'Formação Acadêmica',
      academic: [
        { title: 'Licenciado em Medicina e Cirurgia', desc: 'Universidade de Granada' },
        { title: 'Especialização em Cirurgia Plástica', desc: 'Formação no Brasil com os melhores especialistas' },
        { title: 'Mestrado em Medicina Estética', desc: 'Formação contínua e atualização permanente' },
      ],
      experienceTitle: 'Experiência Profissional',
      experience: [
        { title: '+30 anos de experiência', desc: 'Em cirurgia estética e medicina estética' },
        { title: 'Pioneiro em Rinomodelação na Espanha', desc: 'Realizando esta técnica desde 2007' },
        { title: 'Prática privada', desc: 'Em Marbella, Madrid e Barcelona' },
      ],
      recognitionsTitle: 'Reconhecimentos e Afiliações',
      recognitions: [
        { title: 'Sociedade Espanhola de Medicina Estética (SEME)', desc: 'Membro ativo e palestrante em congressos' },
        { title: 'Colégio de Médicos de Málaga', desc: 'Registrado nº XXXX' },
      ],
      internationalTitle: 'Projeção Internacional',
      internationalText: 'O Dr. Norro desenvolveu técnicas próprias de rinomodelação que foram reconhecidas internacionalmente. Ele treinou outros profissionais e participa regularmente de congressos nacionais e internacionais compartilhando sua experiência.',
      cta: 'CONSULTAR COM O DR. NORRO',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion/doctor')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.backTo}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          
          {/* Formación */}
          <div className="bg-ivory p-8 rounded-none shadow-lg mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gold/10 rounded-none flex items-center justify-center">
                <GraduationCap className="text-gold" size={28} />
              </div>
              <h2 className="text-2xl font-light text-[#1C1C1A]">{t.academicTitle}</h2>
            </div>
            <ul className="space-y-4">
              {t.academic.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-gold rounded-none mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-[#1C1C1A]">{item.title}</h4>
                    <p className="text-text-secondary">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Experiencia */}
          <div className="bg-ivory p-8 rounded-none shadow-lg mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gold/10 rounded-none flex items-center justify-center">
                <Briefcase className="text-gold" size={28} />
              </div>
              <h2 className="text-2xl font-light text-[#1C1C1A]">{t.experienceTitle}</h2>
            </div>
            <ul className="space-y-4">
              {t.experience.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-cream0 rounded-none mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-[#1C1C1A]">{item.title}</h4>
                    <p className="text-text-secondary">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Reconocimientos */}
          <div className="bg-ivory p-8 rounded-none shadow-lg mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-amber-100 rounded-none flex items-center justify-center">
                <Award className="text-amber-600" size={28} />
              </div>
              <h2 className="text-2xl font-light text-[#1C1C1A]">{t.recognitionsTitle}</h2>
            </div>
            <ul className="space-y-4">
              {t.recognitions.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-none mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-[#1C1C1A]">{item.title}</h4>
                    <p className="text-text-secondary">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Internacional */}
          <div className="bg-ivory p-8 rounded-none shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-purple-100 rounded-none flex items-center justify-center">
                <Globe className="text-purple-600" size={28} />
              </div>
              <h2 className="text-2xl font-light text-[#1C1C1A]">{t.internationalTitle}</h2>
            </div>
            <p className="text-text-secondary leading-relaxed">
              {t.internationalText}
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded-none"
            >
              {t.cta}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Curriculum;
